import { FORM_TYPE, onSignInSubmit } from '@atlas/react-forms';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';

import { activeFormsRequest, loginRequest } from 'api/forms';
import AuthStaticBlockFallback from 'components/AuthStaticBlockFallback';
import { Form } from 'components/Form';
import AuthLayout from 'components/Layouts/Auth';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import StaticBlock from 'components/StaticBlock';
import { useConfig } from 'config';
import { AuthStatus, useClientProfile, useNotifications } from 'store';
import { useAuthRedirects } from 'utils/authRedirects';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';

import styles from './SignIn.module.scss';

const SignInPage = () => {
  const { redirectAfterAuth } = useAuthRedirects();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formType, setFormType] = useState<FORM_TYPE>(FORM_TYPE.CLIENT_LOGIN);
  const [clientProfile, { auth }] = useClientProfile();
  const [{ add }] = useNotifications();
  const layout = useLayout();
  const staticBlockName = useConfig(
    `config.siteConfig.authForms.banners.signin.${layout}`,
  ) as string | undefined;
  const isMobile = layout === LayoutType.mobile;

  const getActiveForms = useCallback(() => {
    return activeFormsRequest({
      query: {
        formType,
      },
    });
  }, [formType]);

  const handleSubmit = onSignInSubmit((values) => {
    setIsLoading(true);

    const { id, ...rest } = values;

    return loginRequest({
      body: {
        id: Number(id),
        values: rest,
      },
    });
  });

  useEffect(() => {
    if (clientProfile.status === AuthStatus.signedIn) {
      if (isLoading) {
        add({
          text: `Welcome back, ${clientProfile?.data?.client.login}!`,
        });
      }
      redirectAfterAuth();
    }
  }, [isLoading, clientProfile, add, redirectAfterAuth]);

  const onSubmitSuccess = (clientData: Record<PropertyKey, unknown>) => {
    if (clientData) {
      if (clientData?.is2FaEnabled) {
        setFormType(FORM_TYPE.LOGIN_2FA_BY_PHONE);
      }

      if (!clientData?.is2FaEnabled && clientData.sessionToken) {
        auth(String(clientData.sessionToken));
      }
    }
  };

  const dontHaveAnAccount = useMemo(
    () => (
      <div className={styles['form-info']}>
        <Translation message="dont_have_an_account" />{' '}
        <Link className={styles.link} to="/signup">
          <Translation message="register_button" />
        </Link>
      </div>
    ),
    [],
  );

  return (
    <AuthLayout className={styles['sign-in']} title="log_in_title">
      <Meta title={<Translation message="title_signin" />} />
      <StaticBlock
        name={staticBlockName}
        fallback={<AuthStaticBlockFallback />}
      />
      <div className={cx(styles.content, styles[layout])}>
        {!isMobile && (
          <>
            <h3 className={styles.title}>
              <Translation message="login_welcome" />
            </h3>
            <p className={styles.description}>
              <Translation message="log_in" />
            </p>
          </>
        )}
        <Form
          formType={formType}
          className={styles.form}
          // @ts-ignore
          handleSubmit={handleSubmit}
          onSubmit={onSubmitSuccess}
          getActiveForms={getActiveForms}
          submitButtonText="form_submit_log_in"
          onError={() => {
            setIsLoading(false);
          }}
          beforeSubmitButton={
            isLoading ? (
              <Loader />
            ) : (
              <div className={styles['before-submit-button']}>
                <div className={styles['form-info']}>
                  <Translation message="forgot_password" />{' '}
                  <Link
                    className={styles.link}
                    to="/reset-password"
                    onClick={() =>
                      pushToDataLayer('forgot_password_click', btag)
                    }
                  >
                    <Translation message="reset" />
                  </Link>
                </div>
                {!isMobile && dontHaveAnAccount}
              </div>
            )
          }
          afterSubmitButton={isMobile && dontHaveAnAccount}
        />
      </div>
    </AuthLayout>
  );
};

export default SignInPage;
